// Phase 6 — inter-station jump-gate wire protocol.
//
// C→S: requestJump
// S→C: jumpStarted (durable), jumpCompleted (durable), traveling (ephemeral)
//
// All types conform to the {type, ts, payload} envelope per CONTRACTS §2.
// Builders are the only sanctioned construction path.

import type { Envelope } from "./messages.ts";
import type { StationId } from "./stations.ts";

// -----------------------------------------------------------------------
// Client → Server
// -----------------------------------------------------------------------

export type ClientRequestJump = Envelope<"requestJump", { targetStationId: StationId }>;

export const buildRequestJumpMessage = (targetStationId: StationId): ClientRequestJump => ({
  type: "requestJump",
  ts: Date.now(),
  payload: { targetStationId },
});

// -----------------------------------------------------------------------
// Server → Client
// -----------------------------------------------------------------------

// Durable: sent immediately after debit + alarm arm. Client shows travel overlay.
export type ServerJumpStarted = Envelope<"jumpStarted", { target: StationId; completesAt: number }>;

// Durable: sent by the jump-arrival alarm when current_station_id flips.
export type ServerJumpCompleted = Envelope<"jumpCompleted", { stationId: StationId }>;

// Ephemeral: sent only during the resume handshake when
// traveling_to_station_id IS NOT NULL. Tells a reconnecting client
// which jump is in-flight so it can render the overlay while waiting.
export type ServerTraveling = Envelope<"traveling", { target: StationId; completesAt: number }>;

export const buildJumpStartedMessage = (target: StationId, completesAt: number): ServerJumpStarted => ({
  type: "jumpStarted",
  ts: Date.now(),
  payload: { target, completesAt },
});

export const buildJumpCompletedMessage = (stationId: StationId): ServerJumpCompleted => ({
  type: "jumpCompleted",
  ts: Date.now(),
  payload: { stationId },
});

export const buildTravelingMessage = (target: StationId, completesAt: number): ServerTraveling => ({
  type: "traveling",
  ts: Date.now(),
  payload: { target, completesAt },
});
